import { useLocalSearchParamState } from '@Hooks/useLocalSearchParamState';
import { OrderByTypeEmployeeOverview, useTableSorting } from '@Hooks/useOrdering';
import { usePageTitle } from '@Hooks/usePageTitle';
import { useResizeObserver } from '@Hooks/useResizeObserver';
import { runTour } from '@Hooks/useTourMe.ts';
import { AUTH_LEVEL_DEPARTMENT, AUTH_LEVEL_READ_ONLY, useTskContext } from '@Hooks/useTskContext';
import { EmployeeOverviewTable } from '@Page/EmployeeOverview/EmployeeOverviewTable';
import { useEmployeeOverviewFilters } from '@Page/EmployeeOverview/hooks/useEmployeeOverviewFilters';
import { useDownloadExportedFile, useEmployees, useGetAllEmployeeIds } from '@Page/EmployeeOverview/hooks/useEmployees';
import { useSelectableEmployees } from '@Page/EmployeeOverview/hooks/useSelectableEmployees';
import { useTableColumnsSettingTabs } from '@Page/EmployeeOverview/useTableColumnsSettingTabs.ts';
import { InsufficientAccessPage } from '@Page/InsufficientAccessPage/InsufficientAccessPage';
import { getContextId } from '@tlx/astro-shared';
import { Tour } from '@tlx/tourme-core';
import EmployeeOverviewTour from '@TourMe/EmployeeOverviewTour.json';
import EmployeeOverviewTourFiltersOverflow from '@TourMe/EmployeeOverviewTourFiltersOverflow.json';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { Page } from '@Component/Page/Page.tsx';
import { EmployeeOverviewHeader } from '@Page/EmployeeOverview/EmployeeOverviewHeader.tsx';

export type EmployeeOverviewTab = '' | 'salary' | 'taxcard'; //blank is details

export function EmployeeOverview() {
    const intl = useIntl();
    const { ref, size } = useResizeObserver();

    const pageTitle = intl.formatMessage({ id: 'heading_employee_overview' });
    usePageTitle(pageTitle);
    const ordering = useTableSorting<OrderByTypeEmployeeOverview>();
    const downloadExportedFile = useDownloadExportedFile();

    const [isPendingDownload, setIsPendingDownload] = useState(false);

    const [, setCurrentTab] = useLocalSearchParamState('view', '');
    const [validationsMap, setValidationsMap] = useState<Map<number, string[]>>(new Map());
    const { authLevelEmployeeOrWage, isLoading, userDepartmentId, hasDepartment } = useTskContext();

    const navigate = useNavigate();
    const contextId = getContextId();

    const filters = useEmployeeOverviewFilters();
    const [, setDepartmentFilter] = useLocalSearchParamState(filters.departmentFilter.name);

    const [savedDepartmentDropdownBeforeSwitchingFilter, setSavedDepartmentDropdownBeforeSwitchingFilter] =
        useState<string>('');

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const items = useEmployees({
        filters,
        ordering,
        employeeIdsToShowOnTop: Array.from(validationsMap.keys()),
    });
    const columnIds = useTableColumnsSettingTabs(filters).map((column) => column.id);

    const allIds = useGetAllEmployeeIds({
        filters,
        ordering,
        onlyIds: true,
    });
    const checkMachinery = useSelectableEmployees(allIds);
    const { error } = items;

    const handleDownload = async () => {
        setIsPendingDownload(true);
        const employeeOverviewText = intl.formatMessage({
            id: 'heading_employee_overview',
        });

        const fileName = `${employeeOverviewText.replace(/\s+/g, '_')}_${new Date().toLocaleDateString()}.xlsx`;

        try {
            await downloadExportedFile({
                filters,
                ordering,
                employeeIdsToShowOnTop: Array.from(validationsMap.keys()),
                exportProps: {
                    fileName,
                    exportColumns: columnIds.join(','),
                },
            });
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
            return null;
        } finally {
            setIsPendingDownload(false);
        }
    };

    useEffect(() => {
        if (authLevelEmployeeOrWage == AUTH_LEVEL_DEPARTMENT && userDepartmentId != 0 && hasDepartment) {
            //Switching TO salary view, locking to department admins department
            if (filters.viewFilter.query === 'salary') {
                if (filters.departmentFilter.query !== userDepartmentId.toString()) {
                    setSavedDepartmentDropdownBeforeSwitchingFilter(filters.departmentFilter.query);
                    setDepartmentFilter(userDepartmentId.toString());
                }
            } else {
                //switching away from salary view
                setDepartmentFilter(savedDepartmentDropdownBeforeSwitchingFilter);
            }
        }
    }, [filters.viewFilter.query]);

    useEffect(() => {
        if (!isLoading && filters.viewFilter.query === 'salary' && authLevelEmployeeOrWage <= AUTH_LEVEL_READ_ONLY) {
            setCurrentTab('details');
        }
    }, [filters.viewFilter.query, authLevelEmployeeOrWage, isLoading, setCurrentTab]);

    if (!isLoading && authLevelEmployeeOrWage < AUTH_LEVEL_READ_ONLY) {
        return <InsufficientAccessPage />;
    }

    if (error?.details?.status === 422 || error?.details?.status === 404) {
        // Handles wrong parameters
        // You get a 422 if the period/filter is wrong and 404 if the ordering is wrong
        navigate('?contextId=' + contextId);
    }

    if (error?.details?.status === 401 || error?.details?.status === 403) {
        // Don't show anything if not logged in
        // 403 should probably not happen, but to avoid infinite loops just in case
        return null;
    }

    function runEmployeeOverviewTour() {
        checkMachinery.onUncheckAll();

        //Showing a different tour if the quick filters don't have space on the screen
        const filterOverflowing = document
            .querySelector("[data-testid='filter-bar']")
            ?.classList.contains('atl-filter-bar--overflowing');
        if (filterOverflowing) {
            runTour(EmployeeOverviewTourFiltersOverflow as Tour, {
                testMode: true,
                language: intl.locale,
            });
            return;
        }
        runTour(EmployeeOverviewTour as Tour, {
            testMode: true,
            language: intl.locale,
        });
    }

    return (
        <Page ref={ref} className={'employee-overview__page'}>
            <EmployeeOverviewHeader
                pageTitle={pageTitle}
                runEmployeeOverviewTour={runEmployeeOverviewTour}
                width={size?.width ?? 1000}
            />
            <EmployeeOverviewTable
                scrollableContainerRef={scrollContainerRef}
                filterState={filters}
                checkMachinery={checkMachinery}
                items={items}
                ordering={ordering}
                validationsMap={validationsMap}
                setValidationsMap={setValidationsMap}
                handleDownload={handleDownload}
                pendingDownload={isPendingDownload}
            />
        </Page>
    );
}

const redirectUrl = () => {
    const contextId = getContextId();
    return '/execute/listEmployees?contextId=' + contextId;
};

EmployeeOverview.redirectUrl = redirectUrl;
